import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Lock, Loader2, ShieldCheck, Eye, EyeOff, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import api from "../lib/api";

const NCC_LOGO = "/ncc-logo.png";

export default function AdminResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = pw2.length > 0 && pw !== pw2;
  const canSubmit = pw.length >= 8 && pw === pw2 && !!token && !submitting;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#e8eef9] via-[#f5f7fc] to-white">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rotate-12 rounded-3xl bg-gradient-to-br from-[#fbbf24] to-[#f59e0b] opacity-90" />
      <div className="pointer-events-none absolute top-0 right-0 h-48 w-2/3 rounded-bl-[120px] bg-gradient-to-l from-[#1e3a8a] via-[#1e40af] to-transparent opacity-95" />
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-[#1e3a8a]" />
      <div className="pointer-events-none absolute bottom-20 left-0 h-2 w-full bg-[#fbbf24]" />

      <div className="relative mx-auto max-w-md px-5 pt-14 pb-32">
        <div className="flex flex-col items-center text-center">
          <img src={NCC_LOGO} alt="NCC-SWMS · MANPOWER DATABANK" className="w-52 max-w-full" />
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg" data-testid="reset-card">
          {!token ? (
            <div className="flex items-start gap-3 rounded-md border border-red-300 bg-red-50 p-3">
              <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />
              <div>
                <div className="font-bold text-red-700">Missing reset token</div>
                <p className="mt-1 text-sm text-red-700">This link is invalid. Please request a fresh password reset from the login page.</p>
                <Link to="/admin/login" className="mt-3 inline-block text-sm font-semibold text-[#1e3a8a] hover:underline">← Back to login</Link>
              </div>
            </div>
          ) : done ? (
            <div className="flex flex-col items-center text-center" data-testid="reset-success">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h2 className="mt-4 font-heading text-2xl font-extrabold text-[#1e3a8a]">Password reset</h2>
              <p className="mt-1 text-sm text-slate-600">Your password has been updated. Sign in with your new password.</p>
              <button
                onClick={() => nav("/admin/login")}
                data-testid="reset-back-to-login"
                className="mt-5 flex items-center gap-2 rounded-lg bg-[#1e3a8a] px-5 py-2.5 text-sm font-bold tracking-wider text-white hover:-translate-y-0.5 transition-transform"
              >
                Go to login <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#eef2ff]">
                  <Lock className="h-6 w-6 text-[#1e3a8a]" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-extrabold text-[#1e3a8a]">Set a new password</h2>
                  <p className="text-sm text-slate-500">Choose a strong password (min 8 characters)</p>
                </div>
              </div>

              <form onSubmit={submit} className="mt-5" data-testid="reset-form">
                <label className="block">
                  <span className="text-xs font-semibold tracking-wider text-slate-600">NEW PASSWORD</span>
                  <div className="relative mt-1">
                    <input
                      type={showPw ? "text" : "password"}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      data-testid="reset-password"
                      className={`w-full rounded-lg border bg-white py-3 px-3 pr-11 text-sm focus:outline-none focus:ring-2 ${tooShort ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]/15"}`}
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:bg-slate-100" aria-label="toggle visibility">
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {tooShort && <div className="mt-1 text-xs text-red-600">At least 8 characters</div>}
                </label>

                <label className="mt-3 block">
                  <span className="text-xs font-semibold tracking-wider text-slate-600">CONFIRM PASSWORD</span>
                  <input
                    type={showPw ? "text" : "password"}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    required
                    autoComplete="new-password"
                    data-testid="reset-password-confirm"
                    className={`mt-1 w-full rounded-lg border bg-white py-3 px-3 text-sm focus:outline-none focus:ring-2 ${mismatch ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]/15"}`}
                  />
                  {mismatch && <div className="mt-1 text-xs text-red-600">Passwords don&apos;t match</div>}
                </label>

                {error && <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="reset-error">{error}</div>}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="reset-submit"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-[#1e3a8a] py-3 text-sm font-bold tracking-wider text-white shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {submitting ? "UPDATING…" : "UPDATE PASSWORD"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/admin/login" className="text-xs font-semibold text-[#1e3a8a] hover:underline">← Back to login</Link>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-[#1e3a8a] py-3 text-white">
        <div className="mx-auto flex max-w-md items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-full bg-white">
              <ShieldCheck className="h-4 w-4 text-[#1e3a8a]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold">NCC Limited</div>
              <div className="text-[10px] tracking-wider text-blue-200">Building India&apos;s Future</div>
            </div>
          </div>
          <div className="text-[10px] tracking-wider text-blue-200">Version 1.0.0</div>
        </div>
      </div>
    </div>
  );
}
